/**
 * Patient Profile Form Component
 * Handles patient profile creation and updates during onboarding
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Skeleton,
  Divider
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-toastify';

// API Services
import { patientOnboardingService, formatProfileData, validateProfile } from '../../services/api/patientOnboarding';
import { comprehensiveProfileApi } from '../../services/api/patientComprehensive';

// Utils
import { getSuccessMessage, handleApiError, handleValidationErrors } from '../../utils/errorHandler';

const PatientProfileForm = ({ onSaved, onSkip, initialData }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    dateOfBirth: null,
    gender: '',
    phoneE164: '',
    email: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    countryCode: 'IN',
    emergencyContactName: '',
    emergencyContactPhone: '',
    bloodGroup: '',
    maritalStatus: '',
    education: '',
    employmentStatus: '',
    occupation: '',
    employer: '',
    insuranceProvider: '',
    insurancePolicyNumber: '',
    insuranceGroupNumber: ''
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileExists, setProfileExists] = useState(false);

  useEffect(() => {
    initializeForm();
  }, [initialData]);

  const initializeForm = async () => {
    try {
      setLoading(true);
      
      // Try to get existing profile data
      try {
        const existingProfile = await patientOnboardingService.getProfile();
        if (existingProfile && existingProfile.full_name) {
          setProfileExists(true);
          setFormData(prev => ({
            ...prev,
            fullName: existingProfile.full_name || '',
            dateOfBirth: existingProfile.dob ? new Date(existingProfile.dob) : null,
            gender: existingProfile.gender || '',
            phoneE164: existingProfile.phone_e164 || '',
            email: existingProfile.email || '',
            addressLine1: existingProfile.address_line1 || '',
            addressLine2: existingProfile.address_line2 || '',
            city: existingProfile.city || '',
            state: existingProfile.state || '',
            postalCode: existingProfile.postal_code || '',
            countryCode: existingProfile.country_code || 'IN',
            emergencyContactName: existingProfile.emergency_contact_name || '',
            emergencyContactPhone: existingProfile.emergency_contact_phone_e164 || '',
            bloodGroup: existingProfile.blood_group || '',
            maritalStatus: existingProfile.marital_status || '',
            education: existingProfile.education || '',
            employmentStatus: existingProfile.employment_status || '',
            occupation: existingProfile.occupation || '',
            employer: existingProfile.employer || '',
            insuranceProvider: existingProfile.insurance_provider || '',
            insurancePolicyNumber: existingProfile.insurance_policy_number || '',
            insuranceGroupNumber: existingProfile.insurance_group_number || ''
          }));
        }
      } catch (profileErr) {
        console.log('No existing profile found, starting fresh');
      }
      
      // Merge with initial data if provided
      if (initialData) {
        setFormData(prev => ({
          ...prev,
          ...initialData
        }));
      }
      
    } catch (err) {
      console.error('Error initializing form:', err);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // MINIMAL REQUIRED FIELDS - Only name and phone for identification
    if (!formData.fullName || !formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required for identification';
    } else {
      const fullNameError = validateProfile.fullName(formData.fullName);
      if (fullNameError) newErrors.fullName = fullNameError;
    }
    
    if (!formData.phoneE164 || !formData.phoneE164.trim()) {
      newErrors.phoneE164 = 'Phone number is required for contact';
    } else {
      const phoneError = validateProfile.phoneE164(formData.phoneE164);
      if (phoneError) newErrors.phoneE164 = phoneError;
    }
    
    // Optional field validations (only if provided)
    if (formData.dateOfBirth) {
      const dobError = validateProfile.dateOfBirth(formData.dateOfBirth);
      if (dobError) newErrors.dateOfBirth = dobError;
    }
    
    if (formData.email && formData.email.trim()) {
      const emailError = validateProfile.email(formData.email);
      if (emailError) newErrors.email = emailError;
    }
    
    if (formData.postalCode && formData.postalCode.trim()) {
      const postalError = validateProfile.postalCode(formData.postalCode);
      if (postalError) newErrors.postalCode = postalError;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    console.log('🔄 Starting save process...');
    console.log('Form data:', formData);
    
    if (!validateForm()) {
      console.log('❌ Validation failed:', errors);
      handleValidationErrors(errors, toast);
      return;
    }
    
    try {
      setSaving(true);
      console.log('✅ Validation passed, proceeding with save...');
      
      // Format data for comprehensive API
      const comprehensiveData = {
        full_name: formData.fullName,
        dob: formData.dateOfBirth ? formData.dateOfBirth.toISOString().split('T')[0] : null,
        gender: formData.gender,
        phone_e164: formData.phoneE164,
        email: formData.email,
        address_line1: formData.addressLine1,
        address_line2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postalCode,
        country_code: formData.countryCode,
        emergency_contact_name: formData.emergencyContactName,
        emergency_contact_phone_e164: formData.emergencyContactPhone,
        blood_group: formData.bloodGroup,
        marital_status: formData.maritalStatus,
        education: formData.education,
        employment_status: formData.employmentStatus,
        occupation: formData.occupation,
        employer: formData.employer,
        insurance_provider: formData.insuranceProvider,
        insurance_policy_number: formData.insurancePolicyNumber,
        insurance_group_number: formData.insuranceGroupNumber
      };
      
      console.log('📤 Sending data to API:', comprehensiveData);
      
      // Try comprehensive API first
      try {
        const response = await comprehensiveProfileApi.updateComprehensiveProfile(comprehensiveData);
        console.log('✅ Comprehensive API success:', response);
        toast.success(getSuccessMessage('profile_saved'));
        
        // Call onSaved callback if provided
        if (onSaved) {
          onSaved(response);
        } else {
          // Redirect to profile page after successful save
          window.location.href = '/patient-dashboard/profile';
        }
        return;
      } catch (comprehensiveErr) {
        console.log('⚠️ Comprehensive API failed, falling back to basic profile API:', comprehensiveErr);
      }
      
      // Fallback to basic profile API
      const profileData = formatProfileData(formData);
      console.log('📤 Sending to basic API:', profileData);
      const response = await patientOnboardingService.saveProfile(profileData);
      console.log('✅ Basic API success:', response);
      
      // Show success message
      toast.success(getSuccessMessage('profile_saved'));
      
      // Call onSaved callback if provided
      if (onSaved) {
        onSaved(response);
      } else {
        // Redirect to profile page after successful save
        window.location.href = '/patient-dashboard/profile';
      }
      
    } catch (err) {
      console.error('❌ Error saving profile:', err);
      
      // Handle API validation errors
      if (err.response?.data?.details) {
        const apiErrors = {};
        const details = err.response.data.details;
        
        // Map API field names to form field names
        Object.keys(details).forEach(apiField => {
          const formField = mapApiFieldToFormField(apiField);
          if (formField) {
            apiErrors[formField] = Array.isArray(details[apiField]) 
              ? details[apiField][0] 
              : details[apiField];
          }
        });
        
        setErrors(apiErrors);
        console.log('❌ API validation errors:', apiErrors);
      }
      
      // Show user-friendly error message
      handleApiError(err, 'profile_save', toast);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    console.log('⏭️ Skipping profile form...');
    console.log('🔍 Current user state:', { onSkip, user: window.user });
    
    try {
      // Mark profile section as complete when skipping
      console.log('📡 Calling completeSection API...');
      const result = await patientOnboardingService.completeSection('profile');
      console.log('✅ Profile section marked as complete:', result);
      
      if (onSkip) {
        console.log('🔄 Calling onSkip callback...');
        onSkip();
      } else {
        console.log('🔄 Redirecting to dashboard...');
        window.location.href = '/patient-dashboard';
      }
    } catch (error) {
      console.error('❌ Error marking profile as complete:', error);
      console.log('🔄 Proceeding with skip despite API error...');
      
      // Still proceed with skip even if API call fails
      if (onSkip) {
        console.log('🔄 Calling onSkip callback (fallback)...');
        onSkip();
      } else {
        console.log('🔄 Redirecting to dashboard (fallback)...');
        window.location.href = '/patient-dashboard';
      }
    }
  };

  const mapApiFieldToFormField = (apiField) => {
    const mapping = {
      'full_name': 'fullName',
      'dob': 'dateOfBirth',
      'phone_e164': 'phoneE164',
      'email': 'email',
      'postal_code': 'postalCode',
      'blood_group': 'bloodGroup',
      'marital_status': 'maritalStatus',
      'employment_status': 'employmentStatus',
      'insurance_provider': 'insuranceProvider',
      'insurance_policy_number': 'insurancePolicyNumber',
      'insurance_group_number': 'insuranceGroupNumber'
    };
    return mapping[apiField] || apiField;
  };

  const calculateCompletionPercentage = () => {
    // MINIMAL REQUIRED FIELDS - Only name and phone for identification
    const requiredFields = [
      'fullName', 'phoneE164'
    ];
    
    const optionalFields = [
      'email', 'dateOfBirth', 'gender', 'addressLine1', 'city', 'state', 
      'emergencyContactName', 'emergencyContactPhone', 'bloodGroup', 'maritalStatus', 
      'education', 'employmentStatus', 'occupation', 'employer', 'insuranceProvider', 
      'insurancePolicyNumber', 'insuranceGroupNumber'
    ];
    
    const allFields = [...requiredFields, ...optionalFields];
    
    const completedFields = allFields.filter(field => {
      const value = formData[field];
      return value && value.toString().trim().length > 0;
    });
    
    // Weight required fields more heavily (70% for required, 30% for optional)
    const requiredCompleted = requiredFields.filter(field => {
      const value = formData[field];
      return value && value.toString().trim().length > 0;
    }).length;
    
    const optionalCompleted = optionalFields.filter(field => {
      const value = formData[field];
      return value && value.toString().trim().length > 0;
    }).length;
    
    // Calculate weighted percentage (70% for required, 30% for optional)
    const requiredPercentage = (requiredCompleted / requiredFields.length) * 70;
    const optionalPercentage = (optionalCompleted / optionalFields.length) * 30;
    
    return Math.round(requiredPercentage + optionalPercentage);
  };

  const completionPercentage = calculateCompletionPercentage();

  if (loading) {
    return (
      <Box p={3}>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={3}>
        <Card>
          <CardContent>
            {/* Header */}
            <Box mb={3}>
              <Typography variant="h4" gutterBottom>
                {profileExists ? 'Update Your Profile' : 'Complete Your Profile'}
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Help us provide you with the best healthcare experience by completing your profile.
              </Typography>
              
              {/* Progress Indicator */}
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Profile Completion: {completionPercentage}%
                </Typography>
                <Box 
                  sx={{ 
                    width: '100%', 
                    height: 8, 
                    backgroundColor: 'grey.200', 
                    borderRadius: 4,
                    mt: 1
                  }}
                >
                  <Box 
                    sx={{ 
                      width: `${completionPercentage}%`, 
                      height: '100%', 
                      backgroundColor: completionPercentage >= 70 ? 'success.main' : 'warning.main',
                      borderRadius: 4,
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Basic Information *
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Full Name"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  error={!!errors.fullName}
                  helperText={errors.fullName || 'Your complete name as it appears on official documents'}
                  placeholder="Enter your full name"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Phone Number"
                  value={formData.phoneE164}
                  onChange={(e) => handleInputChange('phoneE164', e.target.value)}
                  error={!!errors.phoneE164}
                  helperText={errors.phoneE164 || 'E.164 format: +911234567890'}
                  placeholder="+911234567890"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Date of Birth"
                  value={formData.dateOfBirth}
                  onChange={(date) => handleInputChange('dateOfBirth', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.dateOfBirth,
                      helperText: errors.dateOfBirth || 'Optional - helps us provide age-appropriate care'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    label="Gender"
                  >
                    <MenuItem value="">Select Gender</MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                    <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email || 'Optional - for appointment reminders and updates'}
                  placeholder="your.email@example.com"
                />
              </Grid>

              {/* Address Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Address Information (Optional)
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={formData.addressLine1}
                  onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                  placeholder="Street address, P.O. box, company name, c/o"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={formData.addressLine2}
                  onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                  placeholder="Apartment, suite, unit, building, floor, etc."
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="City"
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="State/Province"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  placeholder="State or Province"
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  error={!!errors.postalCode}
                  helperText={errors.postalCode || 'ZIP or postal code'}
                  placeholder="12345"
                />
              </Grid>

              {/* Emergency Contact */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Emergency Contact (Optional)
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact Name"
                  value={formData.emergencyContactName}
                  onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                  placeholder="Full name of emergency contact"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact Phone"
                  value={formData.emergencyContactPhone}
                  onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                  placeholder="+911234567890"
                />
              </Grid>

              {/* Medical Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Medical Information (Optional)
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Blood Group</InputLabel>
                  <Select
                    value={formData.bloodGroup}
                    onChange={(e) => handleInputChange('bloodGroup', e.target.value)}
                    label="Blood Group"
                  >
                    <MenuItem value="">Select Blood Group</MenuItem>
                    <MenuItem value="A+">A+</MenuItem>
                    <MenuItem value="A-">A-</MenuItem>
                    <MenuItem value="B+">B+</MenuItem>
                    <MenuItem value="B-">B-</MenuItem>
                    <MenuItem value="AB+">AB+</MenuItem>
                    <MenuItem value="AB-">AB-</MenuItem>
                    <MenuItem value="O+">O+</MenuItem>
                    <MenuItem value="O-">O-</MenuItem>
                    <MenuItem value="unknown">Unknown</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Marital Status</InputLabel>
                  <Select
                    value={formData.maritalStatus}
                    onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
                    label="Marital Status"
                  >
                    <MenuItem value="">Select Marital Status</MenuItem>
                    <MenuItem value="single">Single</MenuItem>
                    <MenuItem value="married">Married</MenuItem>
                    <MenuItem value="divorced">Divorced</MenuItem>
                    <MenuItem value="widowed">Widowed</MenuItem>
                    <MenuItem value="separated">Separated</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Education & Employment */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Education & Employment (Optional)
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Education Level</InputLabel>
                  <Select
                    value={formData.education}
                    onChange={(e) => handleInputChange('education', e.target.value)}
                    label="Education Level"
                  >
                    <MenuItem value="">Select Education Level</MenuItem>
                    <MenuItem value="primary">Primary School</MenuItem>
                    <MenuItem value="secondary">Secondary School</MenuItem>
                    <MenuItem value="higher_secondary">Higher Secondary</MenuItem>
                    <MenuItem value="bachelor">Bachelor's Degree</MenuItem>
                    <MenuItem value="master">Master's Degree</MenuItem>
                    <MenuItem value="doctorate">Doctorate</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Employment Status</InputLabel>
                  <Select
                    value={formData.employmentStatus}
                    onChange={(e) => handleInputChange('employmentStatus', e.target.value)}
                    label="Employment Status"
                  >
                    <MenuItem value="">Select Employment Status</MenuItem>
                    <MenuItem value="employed">Employed</MenuItem>
                    <MenuItem value="unemployed">Unemployed</MenuItem>
                    <MenuItem value="student">Student</MenuItem>
                    <MenuItem value="retired">Retired</MenuItem>
                    <MenuItem value="self_employed">Self Employed</MenuItem>
                    <MenuItem value="homemaker">Homemaker</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Occupation"
                  value={formData.occupation}
                  onChange={(e) => handleInputChange('occupation', e.target.value)}
                  placeholder="Your profession or job title"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Employer"
                  value={formData.employer}
                  onChange={(e) => handleInputChange('employer', e.target.value)}
                  placeholder="Company or organization name"
                />
              </Grid>

              {/* Insurance Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Insurance Information (Optional)
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Insurance Provider"
                  value={formData.insuranceProvider}
                  onChange={(e) => handleInputChange('insuranceProvider', e.target.value)}
                  placeholder="Insurance company name"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Policy Number"
                  value={formData.insurancePolicyNumber}
                  onChange={(e) => handleInputChange('insurancePolicyNumber', e.target.value)}
                  placeholder="Insurance policy number"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Group Number"
                  value={formData.insuranceGroupNumber}
                  onChange={(e) => handleInputChange('insuranceGroupNumber', e.target.value)}
                  placeholder="Group number (if applicable)"
                />
              </Grid>
            </Grid>

            {/* Actions */}
            <Box mt={4} display="flex" gap={2} justifyContent="space-between">
              <Button
                variant="outlined"
                onClick={handleSkip}
                disabled={saving}
                size="large"
              >
                Skip for now
              </Button>
              
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  size="large"
                >
                  {saving ? 'Saving...' : (profileExists ? 'Update Profile' : 'Save & Continue')}
                </Button>
              </Box>
            </Box>

            {/* Helper Text */}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                💡 <strong>Tip:</strong> You can complete your profile later from the dashboard. 
                Required fields are marked with an asterisk (*).
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default PatientProfileForm;